// A job from the OLD system, brought in through the bid process (Pat, Oct 7 2026: "we are creating
// a lot of jobs that were quoted before the new app — when we start a new job I'd like it to come from
// the bid process; a third choice, New Project From Old System"). One step at a time, every step
// checked before the work order (or job) is made:
//   1 Documents   drag and drop the old files, give each a category and a name (the Documents tile)
//   2 Details     "✦ Read the details from the quote" fills name, customer, contact, location, PM,
//                 priced as and the number (AI — the Oct 7 2026 exception, Ryan); the person checks
//                 each one; work order or job; "Does this project already have a number?"
//   3 Fieldwire   link the Fieldwire project the job already has — or send plans to a new one
//   4 Line items  Import Old Quote / Extract with AI for the lines; the Scope (already read with the
//                 details, or read here) — everything editable before it's kept
//   5 Make it     a checklist of the above, then the work order / job is made from the bid
// Documents come first (Pat, Oct 7 2026: "do documents then details, and use the read quote to get most
// all of the details and have the person confirm"), so the bid is made as the window opens — the files
// need somewhere to go. Cancelled before anything went in, that empty bid is removed again. Closed
// later, the work is kept: "Continue setting it up" on the bid page opens the window again.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../services/apiClient';
import DocumentsPillow, { FieldwirePlans } from './DocumentsPillow';
import { LocationPickerModal, ExtractModal, itemEffectiveTotal, ROUND_NONE } from './AdminBidDetail';
import { PRICE_STRUCTURES, WORK_TYPES } from '../../utils/bidTypes';

const STEPS = ['Documents', 'Details', 'Fieldwire', 'Line items & scope', 'Make it'];
// The name the bid carries until the details are saved (the server's empty-draft cleanup checks for it).
const PLACEHOLDER = 'New project from the old system';
const isPlaceholder = name => String(name || '').startsWith(PLACEHOLDER);
const money = n => '$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const inp = { border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 10px', fontSize: 13.5, fontFamily: 'inherit', boxSizing: 'border-box', width: '100%', background: '#fff' };
const lbl = { fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4, display: 'block' };
const btn = (bg = '#fff', fg = '#374151', bd = '#e5e7eb') => ({ padding: '9px 16px', borderRadius: 9, border: `1px solid ${bd}`, background: bg, color: fg, fontSize: 13, fontWeight: 800, cursor: 'pointer' });
const chip = on => ({ padding: '8px 14px', borderRadius: 999, border: `2px solid ${on ? '#1e3a5f' : '#e5e7eb'}`, background: on ? '#eef2ff' : '#fff', color: on ? '#1e3a5f' : '#374151', fontSize: 13, fontWeight: 800, cursor: 'pointer' });
const readable = d => d && d.filename && !String(d.id || '').includes(':') && !d.generated && /\.(pdf|xlsx|xlsm|xls|csv)$/i.test(d.original_name || d.title || d.filename || '');
const docsOf = d => (Array.isArray(d) ? d : (d && d.documents) || []);
const norm = s => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

export default function OldJobWizard({ bidId: startBidId = null, onClose, onCreated }) {
  const [step, setStep] = useState(0);
  const [bidId, setBidId] = useState(startBidId);
  const [bid, setBid] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [clients, setClients] = useState([]);
  const [pms, setPms] = useState([]);
  const [map, setMap] = useState(false);
  const [f, setF] = useState({ job_name: '', client_name: '', client_id: null, contact_name: '', contact_email: '', contact_phone: '', job_location: '', job_lat: null, job_lng: null, bid_pm_id: '',
    price_structure: 'lump_sum', work_type: 'general', division: 'small', has_number: null, number: '' });
  const [numCheck, setNumCheck] = useState(null);   // { taken, error } for a typed number
  const [nextNo, setNextNo] = useState('');
  const [quoteDocs, setQuoteDocs] = useState([]);
  const [readFrom, setReadFrom] = useState('');
  const [fromQuote, setFromQuote] = useState({});   // which boxes the reader filled — to be checked
  const [readNote, setReadNote] = useState('');
  const [scopeRead, setScopeRead] = useState(null); // { scope, exclusions } read with the details, for step 4
  const madeHere = useRef(false);
  const set = (k, v) => { setF(x => ({ ...x, [k]: v })); setFromQuote(q => (q[k] ? { ...q, [k]: false } : q)); };

  const loadBid = useCallback(async id => {
    const b = await apiFetch(`/bids/${id}`);
    setBid(b);
    return b;
  }, []);
  // A new one: make the bid now, so the files have somewhere to go.
  const starting = useRef(false);
  useEffect(() => {
    apiFetch('/clients').then(c => setClients(Array.isArray(c) ? c : [])).catch(() => {});
    apiFetch('/users').then(u => setPms((Array.isArray(u) ? u : []).filter(x => ['ProjectManager', 'Executive'].includes(x.role) && (x.status || 'active') === 'active'))).catch(() => {});
    if (startBidId) {
      loadBid(startBidId).then(b => setF(x => ({ ...x, job_name: isPlaceholder(b.job_name) ? '' : (b.job_name || ''), client_name: b.client_name || '', client_id: b.client_id || null,
        contact_name: b.client_contact || '', contact_email: b.client_email || '', contact_phone: b.client_phone || '', job_location: b.job_location || '',
        job_lat: b.job_lat ?? null, job_lng: b.job_lng ?? null, bid_pm_id: b.bid_pm_id || '', price_structure: b.price_structure || 'lump_sum', work_type: b.work_type || 'general',
        division: b.old_division || 'small', has_number: b.old_job_number ? true : (isPlaceholder(b.job_name) ? null : false), number: b.old_job_number || '' }))).catch(e => setErr(e?.error || 'Could not open the bid.'));
    } else if (!starting.current) {
      starting.current = true;
      const stamp = new Date().toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
      apiFetch('/bids', { method: 'POST', body: JSON.stringify({ job_name: `${PLACEHOLDER} (${stamp})`, old_system: 1, job_type: 'Sub-Contractor', price_structure: 'lump_sum', work_type: 'general' }) })
        .then(b => { madeHere.current = true; setBidId(b.id); setBid(b); onCreated && onCreated(b); })
        .catch(e => setErr(e?.error || 'Could not start — try again.'));
    }
  }, [startBidId, loadBid]); // eslint-disable-line react-hooks/exhaustive-deps
  // Closing: an empty bid this window made goes away again (the server only removes it if nothing went in).
  async function close() {
    if (madeHere.current && bidId && isPlaceholder(bid?.job_name)) { try { await apiFetch(`/bids/${bidId}/old-system-draft`, { method: 'DELETE' }); } catch { /* it has files — it stays */ } }
    onClose();
  }
  // The quote files on the bid, for "Read the details from the quote".
  useEffect(() => {
    if (step !== 1 || !bidId) return;
    apiFetch(`/bids/${bidId}/documents`).then(d => {
      const list = docsOf(d).filter(readable);
      setQuoteDocs(list);
      setReadFrom(cur => cur || (list.find(x => x.doc_type === 'quote') || list[0] || {}).id || '');
    }).catch(() => {});
  }, [step, bidId]);
  // The next number in the app's series, for "no number yet".
  useEffect(() => {
    apiFetch(`/bids/next-number?division=${f.division}&year=${new Date().getFullYear()}`).then(r => setNextNo(r && r.number ? r.number : '')).catch(() => setNextNo(''));
  }, [f.division]);
  // A typed number is checked as it's typed — one number, one job.
  useEffect(() => {
    if (!f.has_number || !f.number.trim()) { setNumCheck(null); return; }
    const t = setTimeout(() => apiFetch(`/projects/number-check?number=${encodeURIComponent(f.number.trim())}`).then(setNumCheck).catch(() => setNumCheck(null)), 350);
    return () => clearTimeout(t);
  }, [f.has_number, f.number]);

  const jobNumber = f.has_number ? f.number.trim().toUpperCase() : nextNo;
  const kindWord = f.division === 'small' ? 'work order' : 'job';

  // ── step 2: read the details off the quote, for the person to check ──
  async function readDetails() {
    if (!readFrom) return;
    setBusy('read'); setErr(''); setReadNote('');
    try {
      const r = await apiFetch(`/bids/${bidId}/documents/${readFrom}/read-scope`, { method: 'POST' });
      const got = {};
      const put = (k, v) => { if (v != null && String(v).trim() !== '') { got[k] = String(v).trim(); } };
      put('job_name', r.job_name); put('client_name', r.customer); put('contact_name', r.contact_name); put('contact_email', r.contact_email); put('contact_phone', r.contact_phone);
      put('job_location', r.job_location);
      if (r.priced_as) got.price_structure = r.priced_as;
      // The customer, spelled the way the Customer List has it.
      if (got.client_name) { const c = clients.find(x => norm(x.name) === norm(got.client_name)) || clients.find(x => norm(x.name).includes(norm(got.client_name)) || norm(got.client_name).includes(norm(x.name))); if (c) got.client_name = c.name; }
      // Our person who wrote the quote → the PM, when they are one.
      if (r.estimator) { const ln = norm(r.estimator); const u = pms.find(x => ln.includes(norm(x.lastName || x.last_name)) && ln.includes(norm(x.firstName || x.first_name))) || pms.find(x => norm(x.lastName || x.last_name) && ln.includes(norm(x.lastName || x.last_name))); if (u) got.bid_pm_id = u.id; }
      const patch = { ...got };
      if (r.our_job_number) { patch.has_number = true; patch.number = String(r.our_job_number).toUpperCase(); patch.division = /^WO/i.test(r.our_job_number) ? 'small' : f.division; }
      setF(x => ({ ...x, ...patch, ...(got.job_location ? { job_lat: null, job_lng: null } : {}) }));
      setFromQuote(Object.fromEntries(Object.keys(patch).map(k => [k, true])));
      if (r.scope || r.exclusions) setScopeRead({ scope: r.scope || '', exclusions: r.exclusions || '' });
      const n = Object.keys(patch).length;
      setReadNote(n ? `✨ Read by AI — ${n} box${n === 1 ? '' : 'es'} filled from the quote (outlined in amber). Check each one before you save.${r.quote_number ? ` Quote #${r.quote_number}.` : ''}${r.total != null ? ` Quoted total ${money(r.total)}.` : ''}` : 'Nothing could be read off that file — fill the details in.');
    } catch (e) { setErr(e?.error || 'Could not read the quote — fill the details in.'); }
    setBusy('');
  }

  async function saveDetails() {
    setErr('');
    if (!f.job_name.trim()) return setErr('Give the job a name.');
    if (f.has_number == null) return setErr('Answer "Does this project already have a number?"');
    if (f.has_number && !f.number.trim()) return setErr('Type the number it already has.');
    if (f.has_number && numCheck && numCheck.taken) return setErr(numCheck.error || 'That number is already a job.');
    const match = clients.find(c => String(c.name || '').trim().toLowerCase() === f.client_name.trim().toLowerCase());
    const body = { job_name: f.job_name.trim(), client_name: f.client_name.trim() || null, client_id: match ? match.id : null,
      client_contact: f.contact_name.trim() || null, client_email: f.contact_email.trim() || null, client_phone: f.contact_phone.trim() || null,
      job_location: f.job_location || null, job_lat: f.job_lat, job_lng: f.job_lng, bid_pm_id: f.bid_pm_id || null, price_structure: f.price_structure, work_type: f.work_type, job_type: 'Sub-Contractor',
      old_system: 1, old_job_number: f.has_number ? f.number.trim() : null, old_division: f.division };
    setBusy('save');
    try {
      const b = await apiFetch(`/bids/${bidId}`, { method: 'PATCH', body: JSON.stringify(body) });
      setBid(b); setFromQuote({}); setStep(2);
    } catch (e) { setErr(e?.error || 'Could not save the details.'); }
    setBusy('');
  }

  const go = n => { setErr(''); setStep(n); };
  const hl = k => (fromQuote[k] ? { borderColor: '#f59e0b', background: '#fffbeb', boxShadow: '0 0 0 2px #fde68a' } : {});
  return (
    <div onClick={close} style={{ position: 'fixed', inset: 0, zIndex: 5300, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '3vh 12px', overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 960, maxWidth: '100%', boxShadow: '0 16px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ background: '#1e3a5f', color: '#fff', borderRadius: '14px 14px 0 0', padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 15, fontWeight: 800, flex: 1 }}>📦 New project from the old system{bid && !isPlaceholder(bid.job_name) ? ` — ${bid.job_name}` : ''}</span>
          <button onClick={close} title="Close — the bid keeps what's done so far" style={{ background: 'none', border: 'none', color: '#fff', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        {/* the steps */}
        <div style={{ display: 'flex', gap: 6, padding: '12px 18px 0', flexWrap: 'wrap' }}>
          {STEPS.map((s, i) => (
            <button key={s} onClick={() => bidId && go(i)} disabled={!bidId} style={{ ...chip(step === i), opacity: bidId ? 1 : 0.45, cursor: bidId ? 'pointer' : 'default' }}>{i + 1} · {s}</button>
          ))}
        </div>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          {!bidId && !err && <div style={{ color: '#9ca3af' }}>Starting…</div>}

          {step === 0 && bidId && (
            <>
              <div style={{ fontSize: 13, color: '#4b5563' }}>Start with the old job’s files — the <b>old quote</b> first (the next step reads the details off it), then plans, contract, POs, anything. Pick the category before you drop, and use ✎ on a file to name it.</div>
              <DocumentsPillow bidId={bidId} canEdit manage noFieldwire />
            </>
          )}

          {step === 1 && bidId && (
            <>
              <div style={{ border: '1.5px solid #c7d2fe', background: '#eef2ff', borderRadius: 12, padding: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13.5, fontWeight: 800, color: '#3730a3' }}>✦ Read the details from the quote</span>
                {quoteDocs.length ? (
                  <>
                    <select value={readFrom} onChange={e => setReadFrom(e.target.value)} style={{ ...inp, width: 'auto', flex: '1 1 220px', maxWidth: 380 }}>
                      {quoteDocs.map(d => <option key={d.id} value={d.id}>{d.title || d.original_name}</option>)}
                    </select>
                    <button onClick={readDetails} disabled={busy === 'read'} style={btn('#3730a3', '#fff', '#3730a3')}>{busy === 'read' ? 'Reading… (a minute)' : 'Read it'}</button>
                  </>
                ) : <span style={{ fontSize: 13, color: '#6b7280' }}>Add the old quote on step 1 first — or fill the details in by hand.</span>}
              </div>
              {readNote && <div style={{ fontSize: 13, fontWeight: 700, color: readNote.startsWith('✨') ? '#92400e' : '#6b7280' }}>{readNote}</div>}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
                <div><span style={lbl}>Job name *</span><input value={f.job_name} onChange={e => set('job_name', e.target.value)} style={{ ...inp, ...hl('job_name') }} placeholder="As the old system named it" /></div>
                <div><span style={lbl}>Customer</span>
                  <input value={f.client_name} onChange={e => set('client_name', e.target.value)} list="oldjob-clients" style={{ ...inp, ...hl('client_name') }} placeholder="Pick from the Customer List or type" />
                  <datalist id="oldjob-clients">{clients.map(c => <option key={c.id} value={c.name} />)}</datalist>
                </div>
                <div><span style={lbl}>Location</span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input value={f.job_location} onChange={e => { set('job_location', e.target.value); setF(x => ({ ...x, job_lat: null, job_lng: null })); }} style={{ ...inp, ...hl('job_location') }} placeholder="Address, or pick on the map" />
                    <button type="button" onClick={() => setMap(true)} style={{ ...btn(f.job_lat != null ? '#eff6ff' : '#fff', '#1d4ed8', f.job_lat != null ? '#2563eb' : '#e5e7eb'), whiteSpace: 'nowrap' }}>📍 {f.job_lat != null ? 'Pin set' : 'Pick on Map'}</button>
                  </div>
                </div>
                <div><span style={lbl}>Contact</span><input value={f.contact_name} onChange={e => set('contact_name', e.target.value)} style={{ ...inp, ...hl('contact_name') }} placeholder="Who the quote went to" /></div>
                <div><span style={lbl}>Contact email</span><input value={f.contact_email} onChange={e => set('contact_email', e.target.value)} style={{ ...inp, ...hl('contact_email') }} /></div>
                <div><span style={lbl}>Contact phone</span><input value={f.contact_phone} onChange={e => set('contact_phone', e.target.value)} style={{ ...inp, ...hl('contact_phone') }} /></div>
                <div><span style={lbl}>Project manager</span>
                  <select value={f.bid_pm_id} onChange={e => set('bid_pm_id', e.target.value)} style={{ ...inp, ...hl('bid_pm_id') }}>
                    <option value="">— pick —</option>{pms.map(u => <option key={u.id} value={u.id}>{`${u.firstName || u.first_name || ''} ${u.lastName || u.last_name || ''}`.trim()}</option>)}
                  </select>
                </div>
                <div><span style={lbl}>Priced as</span>
                  <select value={f.price_structure} onChange={e => set('price_structure', e.target.value)} style={{ ...inp, ...hl('price_structure') }}>{PRICE_STRUCTURES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
                </div>
                <div><span style={lbl}>Kind of work</span>
                  <select value={f.work_type} onChange={e => set('work_type', e.target.value)} style={inp}>{WORK_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
                </div>
              </div>
              <div>
                <span style={lbl}>It becomes a</span>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => set('division', 'small')} style={chip(f.division === 'small')}>🧾 Work order</button>
                  <button type="button" onClick={() => set('division', 'large')} style={chip(f.division === 'large')}>🏗️ Job</button>
                </div>
              </div>
              <div style={{ border: '1.5px solid #e5e7eb', borderRadius: 12, padding: 12, ...(fromQuote.number ? { borderColor: '#f59e0b', background: '#fffbeb' } : {}) }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: '#111827', marginBottom: 8 }}>Does this project already have a number?</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button type="button" onClick={() => set('has_number', true)} style={chip(f.has_number === true)}>Yes</button>
                  <button type="button" onClick={() => set('has_number', false)} style={chip(f.has_number === false)}>No</button>
                  {f.has_number === true && (
                    <input value={f.number} onChange={e => set('number', e.target.value)} placeholder={f.division === 'small' ? 'e.g. WO26144' : 'e.g. 2619'} style={{ ...inp, width: 180 }} />
                  )}
                  {f.has_number === false && <span style={{ fontSize: 13, color: '#374151' }}>It gets the next number: <b>{nextNo || '…'}</b></span>}
                </div>
                {f.has_number && numCheck && (numCheck.taken
                  ? <div style={{ fontSize: 12.5, color: '#b91c1c', fontWeight: 700, marginTop: 6 }}>{numCheck.error}</div>
                  : <div style={{ fontSize: 12.5, color: '#15803d', fontWeight: 700, marginTop: 6 }}>✓ {f.number.trim().toUpperCase()} is free</div>)}
              </div>
              {map && <LocationPickerModal value={f.job_location} onConfirm={({ address, lat, lng }) => { setF(x => ({ ...x, job_location: address || x.job_location, job_lat: lat ?? null, job_lng: lng ?? null })); setMap(false); }} onClose={() => setMap(false)} />}
            </>
          )}

          {step === 2 && bidId && <FieldwireStep bidId={bidId} bid={bid} jobNumber={jobNumber} />}

          {step === 3 && bidId && <LinesStep bidId={bidId} bid={bid} reload={() => loadBid(bidId)} prefill={scopeRead} />}

          {step === 4 && bidId && (
            <MakeStep bidId={bidId} bid={bid} f={f} jobNumber={jobNumber} kindWord={kindWord} numCheck={numCheck} go={go}
              onMade={project => window.location.assign(`/admin/jobs/${project.id}`)} />
          )}

          {err && <div style={{ color: '#b91c1c', fontSize: 13, fontWeight: 700 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', borderTop: '1px solid #f1f1f1', paddingTop: 12 }}>
            <button onClick={() => (step === 0 ? close() : go(step - 1))} style={btn()}>{step === 0 ? 'Cancel' : '‹ Back'}</button>
            {step === 1 && <button onClick={saveDetails} disabled={busy === 'save'} style={btn('#1e3a5f', '#fff', '#1e3a5f')}>{busy === 'save' ? 'Saving…' : 'Confirm details & next ›'}</button>}
            {step !== 1 && step < 4 && bidId && <button onClick={() => go(step + 1)} style={btn('#1e3a5f', '#fff', '#1e3a5f')}>Next ›</button>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── step 3: Fieldwire — link the one it already has, or send plans to a new one ──
function FieldwireStep({ bidId, bid, jobNumber }) {
  const [state, setState] = useState(null);
  const [q, setQ] = useState(() => String(jobNumber || '').replace(/^WO/i, '') || String(bid?.job_name || '').split(/\s+/).slice(0, 3).join(' '));
  const [list, setList] = useState(null);
  const [docs, setDocs] = useState([]);
  const [msg, setMsg] = useState('');
  const load = useCallback(() => apiFetch(`/bids/${bidId}/fieldwire`).then(setState).catch(() => setState({ configured: false })), [bidId]);
  useEffect(() => { load(); apiFetch(`/bids/${bidId}/documents`).then(d => setDocs(Array.isArray(d) ? d : (d && d.documents) || [])).catch(() => {}); }, [bidId, load]);
  useEffect(() => {
    // Only the latest search counts — an earlier one answering late must not overwrite it.
    let live = true;
    const t = setTimeout(() => apiFetch(`/fieldwire/projects-list?q=${encodeURIComponent(q)}`).then(r => { if (live) setList(r); }).catch(() => { if (live) setList({ projects: [], total: 0 }); }), 300);
    return () => { live = false; clearTimeout(t); };
  }, [q]);
  async function link(p) {
    setMsg('');
    try { await apiFetch(`/bids/${bidId}/fieldwire/link`, { method: 'POST', body: JSON.stringify({ fw_project_id: p.fw_project_id }) }); await load(); setMsg(`✓ Linked “${p.name}”.`); }
    catch (e) { setMsg(e?.error || 'Could not link that one.'); }
  }
  async function unlink() {
    if (!window.confirm('Take this Fieldwire project off the bid? Nothing changes in Fieldwire.')) return;
    try { await apiFetch(`/bids/${bidId}/fieldwire/link`, { method: 'DELETE' }); await load(); setMsg(''); }
    catch (e) { setMsg(e?.error || 'Could not take it off.'); }
  }
  if (!state) return <div style={{ color: '#9ca3af' }}>Checking Fieldwire…</div>;
  if (state.configured === false) return <div style={{ color: '#6b7280' }}>Fieldwire isn’t connected on this server — skip this step.</div>;
  const linked = state.link;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {linked ? (
        <div style={{ border: '2px solid #bbf7d0', background: '#f0fdf4', borderRadius: 12, padding: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 800, color: '#166534', flex: 1 }}>✓ Fieldwire: {linked.name || linked.fw_project_id}</span>
          <button onClick={unlink} style={btn()}>Take it off</button>
        </div>
      ) : (
        <div style={{ fontSize: 13, color: '#4b5563' }}>Most old jobs already have a Fieldwire project — find it below and link it. Only if it has none, send its plans to a new one further down. Or skip this step.</div>
      )}
      {!linked && (
        <div style={{ border: '1.5px solid #e5e7eb', borderRadius: 12, padding: 12 }}>
          <span style={lbl}>Find the job’s Fieldwire project</span>
          <input value={q} onChange={e => setQ(e.target.value)} style={inp} placeholder="Job number or name" />
          <div style={{ fontSize: 12, color: '#6b7280', margin: '6px 0' }}>{list ? `${list.projects.length} of ${list.total} Fieldwire projects` : 'Searching…'}</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 300, overflowY: 'auto' }}>
            {(list?.projects || []).map(p => (
              <div key={p.fw_project_id} style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #e5e7eb', borderRadius: 9, padding: '8px 10px' }}>
                <span style={{ flex: 1, fontSize: 13.5, fontWeight: 700 }}>{p.name}{p.archived ? <span style={{ color: '#9ca3af', fontWeight: 600 }}> · archived</span> : null}</span>
                {p.linked_to
                  ? <span style={{ fontSize: 12, color: '#6b7280' }}>already on {p.linked_to.kind === 'job' ? 'job' : 'bid'} {p.linked_to.label}</span>
                  : <button onClick={() => link(p)} style={btn('#1e3a5f', '#fff', '#1e3a5f')}>Link</button>}
              </div>
            ))}
            {list && !list.projects.length && <div style={{ fontSize: 13, color: '#9ca3af' }}>No Fieldwire project matches “{q}”.</div>}
          </div>
        </div>
      )}
      <div style={{ border: '1.5px dashed #e5e7eb', borderRadius: 12, padding: 12 }}>
        <span style={lbl}>{linked ? 'Send plans to it' : 'No Fieldwire project yet? Send the plans to a new one'}</span>
        <FieldwirePlans bidId={bidId} docs={docs} canEdit />
      </div>
      {msg && <div style={{ fontSize: 13, fontWeight: 700, color: msg.startsWith('✓') ? '#15803d' : '#b91c1c' }}>{msg}</div>}
    </div>
  );
}

// ── step 4: line items (Import Old Quote / Extract with AI) and the scope (read off the quote) ──
function LinesStep({ bidId, bid, reload, prefill }) {
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState('');
  const [modal, setModal] = useState(null);
  const [msg, setMsg] = useState('');
  const [scope, setScope] = useState(bid?.scope || '');
  const [excl, setExcl] = useState(bid?.exclusions || '');
  const [scopeDirty, setScopeDirty] = useState(false);
  useEffect(() => { apiFetch(`/bids/${bidId}/documents`).then(d => setDocs((Array.isArray(d) ? d : (d && d.documents) || []).filter(readable))).catch(() => {}); }, [bidId]);
  useEffect(() => { if (!scopeDirty) { setScope(bid?.scope || ''); setExcl(bid?.exclusions || ''); } }, [bid?.scope, bid?.exclusions]); // eslint-disable-line react-hooks/exhaustive-deps
  // The scope read with the details on step 2 — filled in, not saved, until the person saves it.
  useEffect(() => {
    if (prefill && prefill.scope && !String(bid?.scope || '').trim()) { setScope(prefill.scope); if (prefill.exclusions) setExcl(prefill.exclusions); setScopeDirty(true); setMsg('✨ Scope filled from the quote by AI — check it, edit it, then Save the scope.'); }
  }, [prefill]); // eslint-disable-line react-hooks/exhaustive-deps
  const items = (bid && bid.items) || [];
  const total = items.reduce((s, it) => s + (Number(itemEffectiveTotal(it, bid)) || 0), 0);

  async function readLines(d, kind) {
    setBusy(d.id + kind); setMsg('');
    try {
      const r = await apiFetch(`/bids/${bidId}/attachments/${d.id}/${kind === 'quote' ? 'import-quote' : 'extract'}`, { method: 'POST' });
      if (!r.items || !r.items.length) setMsg(kind === 'quote' ? 'No priced lines found in that quote.' : 'No line items found in that file.');
      else setModal({ items: r.items, fromQuote: kind === 'quote' });
    } catch (e) { setMsg((e?.error || e?.message || 'Could not read it.').toString()); }
    setBusy('');
  }
  async function addItems(selected) {
    setModal(null);
    const fresh = await apiFetch(`/bids/${bidId}`);
    const have = fresh.items || [];
    const add = selected.map((it, i) => ({
      sort_order: have.length + i, item_number: it.item_number || '', pay_item_number: it.pay_item_number || '', description: it.description || '',
      quantity: it.quantity || 1, unit: it.unit || 'EA', material_cost: it.material_cost || 0, labor_hours: it.labor_hours || 0, notes: it.notes || '',
      // An old quote's line arrives priced — no markups or rounding on top (as on the bid page).
      ...(it.from_quote ? { dje_standard: it.dje_standard || 0, overhead_material_pct: 0, overhead_labor_pct: 0, profit_pct: 0, rounding: ROUND_NONE } : {}),
    }));
    try {
      await apiFetch(`/bids/${bidId}/items`, { method: 'PUT', body: JSON.stringify({ items: [...have, ...add] }) });
      await reload();
      setMsg(`✓ Added ${add.length} line item${add.length === 1 ? '' : 's'}.`);
    } catch (e) { setMsg(e?.error || 'Could not save the line items.'); }
  }
  async function readScope(d) {
    setBusy(d.id + 'scope'); setMsg('');
    try {
      const r = await apiFetch(`/bids/${bidId}/documents/${d.id}/read-scope`, { method: 'POST' });
      setScope(r.scope || ''); if (r.exclusions) setExcl(r.exclusions); setScopeDirty(true);
      setMsg('✨ Scope read by AI — check it, edit it, then Save the scope.');
    } catch (e) { setMsg(e?.error || 'Could not read the scope.'); }
    setBusy('');
  }
  async function saveScope() {
    try { await apiFetch(`/bids/${bidId}`, { method: 'PATCH', body: JSON.stringify({ scope, exclusions: excl }) }); setScopeDirty(false); await reload(); setMsg('✓ Scope saved.'); }
    catch (e) { setMsg(e?.error || 'Could not save the scope.'); }
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ border: '1.5px solid #e5e7eb', borderRadius: 12, padding: 12 }}>
        <span style={lbl}>Read the old quote</span>
        {!docs.length && <div style={{ fontSize: 13, color: '#9ca3af' }}>No PDFs or spreadsheets yet — add the old quote on step 1.</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {docs.map(d => (
            <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', border: '1px solid #f1f1f1', borderRadius: 9, padding: '7px 10px' }}>
              <span style={{ flex: '1 1 240px', fontSize: 13.5, fontWeight: 700 }}>📄 {d.title || d.original_name}</span>
              <button onClick={() => readLines(d, 'quote')} disabled={!!busy} style={btn('#fff', '#1e3a5f', '#1e3a5f')}>{busy === d.id + 'quote' ? 'Reading…' : '📄 Import Old Quote'}</button>
              {/\.pdf$/i.test(d.original_name || d.title || d.filename || '') && <button onClick={() => readLines(d, 'extract')} disabled={!!busy} style={btn('#f5f3ff', '#6d28d9', '#ddd6fe')}>{busy === d.id + 'extract' ? 'Reading…' : '✦ Extract with AI'}</button>}
              <button onClick={() => readScope(d)} disabled={!!busy} style={btn('#f0fdf4', '#166534', '#bbf7d0')}>{busy === d.id + 'scope' ? 'Reading…' : '✦ Read the scope'}</button>
            </div>
          ))}
        </div>
      </div>
      <div style={{ border: '1.5px solid #e5e7eb', borderRadius: 12, padding: 12 }}>
        <span style={lbl}>Line items · {items.length} · {money(total)}</span>
        {items.length ? (
          <div style={{ maxHeight: 220, overflowY: 'auto', fontSize: 12.5 }}>
            {items.map(it => (
              <div key={it.id} style={{ display: 'flex', gap: 8, padding: '3px 0', borderBottom: '1px solid #f3f4f6' }}>
                <span style={{ width: 70, color: '#6b7280' }}>{it.pay_item_number || it.item_number || ''}</span>
                <span style={{ flex: 1 }}>{it.description}</span>
                <span style={{ width: 110, textAlign: 'right' }}>{it.quantity} {it.unit}</span>
                <span style={{ width: 110, textAlign: 'right', fontWeight: 700 }}>{money(itemEffectiveTotal(it, bid))}</span>
              </div>
            ))}
          </div>
        ) : <div style={{ fontSize: 13, color: '#9ca3af' }}>None yet.</div>}
        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 6 }}>To change a line, open the bid page’s Line Items — this list is what will carry to the {bid?.old_division === 'large' ? 'job' : 'work order'}.</div>
      </div>
      <div style={{ border: '1.5px solid #e5e7eb', borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <span style={lbl}>Scope of work</span>
        <textarea value={scope} onChange={e => { setScope(e.target.value); setScopeDirty(true); }} rows={7} style={{ ...inp, resize: 'vertical' }} placeholder="Read it off the old quote above, or type / paste it" />
        <span style={lbl}>Exclusions</span>
        <textarea value={excl} onChange={e => { setExcl(e.target.value); setScopeDirty(true); }} rows={3} style={{ ...inp, resize: 'vertical' }} />
        <div><button onClick={saveScope} disabled={!scopeDirty} style={btn(scopeDirty ? '#1e3a5f' : '#e5e7eb', scopeDirty ? '#fff' : '#9ca3af', scopeDirty ? '#1e3a5f' : '#e5e7eb')}>Save the scope</button></div>
      </div>
      {msg && <div style={{ fontSize: 13, fontWeight: 700, color: msg.startsWith('✓') ? '#15803d' : msg.startsWith('✨') ? '#92400e' : '#b91c1c' }}>{msg}</div>}
      {modal && <ExtractModal items={modal.items} fromQuote={modal.fromQuote} onClose={() => setModal(null)} onAdd={addItems} />}
    </div>
  );
}

// ── step 5: the checklist, then make the work order / job ──
function MakeStep({ bidId, bid, f, jobNumber, kindWord, numCheck, go, onMade }) {
  const [fw, setFw] = useState(undefined);
  const [docCount, setDocCount] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    apiFetch(`/bids/${bidId}/fieldwire`).then(s => setFw(s && s.link ? s.link : null)).catch(() => setFw(null));
    apiFetch(`/bids/${bidId}/documents`).then(d => setDocCount((Array.isArray(d) ? d : (d && d.documents) || []).filter(x => !x.generated).length)).catch(() => setDocCount(0));
  }, [bidId]);
  const items = (bid && bid.items) || [];
  const total = items.reduce((s, it) => s + (Number(itemEffectiveTotal(it, bid)) || 0), 0);
  const rows = [
    { ok: !!(bid && bid.job_name && !isPlaceholder(bid.job_name)), step: 1, label: 'Details', what: `${bid?.job_name || '—'}${bid?.client_name ? ' · ' + bid.client_name : ''}` },
    { ok: !!jobNumber && f.has_number != null && !(f.has_number && numCheck && numCheck.taken), step: 1, label: 'Number', what: jobNumber ? `${jobNumber}${f.has_number ? ' (its own number)' : ' (the next number)'}` : 'none yet' },
    { ok: !!bid?.bid_pm_id, step: 1, label: 'Project manager', what: bid?.bid_pm_id ? 'picked' : 'not picked', soft: true },
    { ok: (docCount || 0) > 0, step: 0, label: 'Documents', what: docCount == null ? '…' : `${docCount} file${docCount === 1 ? '' : 's'}`, soft: true },
    { ok: !!fw, step: 2, label: 'Fieldwire', what: fw === undefined ? '…' : fw ? (fw.name || 'linked') : 'not linked', soft: true },
    { ok: items.length > 0, step: 3, label: 'Line items', what: `${items.length} · ${money(total)}`, soft: true },
    { ok: !!String(bid?.scope || '').trim(), step: 3, label: 'Scope', what: String(bid?.scope || '').trim() ? String(bid.scope).split('\n')[0].slice(0, 80) : 'empty', soft: true },
  ];
  const blocking = rows.filter(r => !r.ok && !r.soft);
  async function make() {
    const soft = rows.filter(r => !r.ok && r.soft).map(r => r.label);
    if (!window.confirm(`Make ${kindWord} ${jobNumber}?${soft.length ? `\n\nStill open: ${soft.join(', ')} — you can finish those on the ${kindWord} afterwards.` : ''}`)) return;
    setBusy(true); setErr('');
    try {
      const r = await apiFetch(`/bids/${bidId}/award`, { method: 'POST', body: JSON.stringify({ division: bid?.old_division || f.division, job_number: jobNumber, job_name: bid?.job_name, pm_id: bid?.bid_pm_id || null }) });
      onMade(r.project);
    } catch (e) {
      setErr(e?.error || 'Could not make it.');
      if (e?.code === 'number_taken' && e.existing && window.confirm(`${e.error}\n\nOpen ${e.existing.project_number} instead?`)) window.location.assign(`/admin/jobs/${e.existing.id}`);
    }
    setBusy(false);
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 13, color: '#4b5563' }}>Check each line. Green is done; amber can be finished later; anything red must be fixed first.</div>
      {rows.map(r => (
        <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 10, border: `1.5px solid ${r.ok ? '#bbf7d0' : r.soft ? '#fde68a' : '#fecaca'}`, background: r.ok ? '#f0fdf4' : r.soft ? '#fffbeb' : '#fef2f2', borderRadius: 10, padding: '9px 12px' }}>
          <span style={{ fontSize: 16 }}>{r.ok ? '✅' : r.soft ? '⚠️' : '⛔'}</span>
          <span style={{ width: 130, fontSize: 13, fontWeight: 800 }}>{r.label}</span>
          <span style={{ flex: 1, fontSize: 13, color: '#374151' }}>{r.what}</span>
          <button onClick={() => go(r.step)} style={{ background: 'none', border: 'none', color: '#1d4ed8', fontSize: 12.5, fontWeight: 800, cursor: 'pointer' }}>Edit ›</button>
        </div>
      ))}
      {err && <div style={{ color: '#b91c1c', fontSize: 13, fontWeight: 700 }}>{err}</div>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
        <a href={`/admin/bids/${bidId}`} style={{ ...btn(), textDecoration: 'none' }}>Open the bid page</a>
        <button onClick={make} disabled={busy || blocking.length > 0} style={btn(blocking.length ? '#cbd5e1' : '#15803d', '#fff', blocking.length ? '#cbd5e1' : '#15803d')}>
          {busy ? 'Making it…' : `Make ${kindWord} ${jobNumber || ''} ›`}
        </button>
      </div>
    </div>
  );
}
